import { doc, setDoc, updateDoc, collection, addDoc, serverTimestamp, query, getDocs, getDoc, orderBy, limit, where, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const saveWorkout = async (userId, workoutData) => {
  try {
    const workoutsRef = collection(db, 'users', userId, 'workouts');
    const docRef = await addDoc(workoutsRef, {
      ...workoutData,
      date: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error saving workout:', error);
    throw error;
  }
};

// In-progress workout management (for recovery from interruptions)
export const createInProgressWorkout = async (userId, sessionData) => {
  try {
    const workoutsRef = collection(db, 'users', userId, 'workouts');
    const docRef = await addDoc(workoutsRef, {
      ...sessionData,
      status: 'in-progress',
      startedAt: serverTimestamp(),
      lastUpdatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating in-progress workout:', error);
    throw error;
  }
};

export const updateInProgressWorkout = async (userId, workoutId, workoutState) => {
  try {
    const workoutRef = doc(db, 'users', userId, 'workouts', workoutId);
    await updateDoc(workoutRef, {
      exercises: workoutState,
      lastUpdatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating in-progress workout:', error);
    throw error;
  }
};

export const getInProgressWorkouts = async (userId) => {
  try {
    const workoutsRef = collection(db, 'users', userId, 'workouts');
    const q = query(
      workoutsRef,
      where('status', '==', 'in-progress'),
      orderBy('startedAt', 'desc'),
      limit(1)
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error('Error getting in-progress workouts:', error);
    return null;
  }
};

export const markWorkoutCompleted = async (userId, workoutId, finalData) => {
  try {
    const workoutRef = doc(db, 'users', userId, 'workouts', workoutId);
    await updateDoc(workoutRef, {
      ...finalData,
      status: 'completed',
      completedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error marking workout completed:', error);
    throw error;
  }
};

export const deleteInProgressWorkout = async (userId, workoutId) => {
  try {
    const workoutRef = doc(db, 'users', userId, 'workouts', workoutId);
    await deleteDoc(workoutRef);
  } catch (error) {
    console.error('Error deleting in-progress workout:', error);
    throw error;
  }
};

export const deleteWorkout = async (userId, workoutId) => {
  try {
    const workoutRef = doc(db, 'users', userId, 'workouts', workoutId);
    await deleteDoc(workoutRef);
  } catch (error) {
    console.error('Error deleting workout:', error);
    throw error;
  }
};

export const deleteExerciseFromWorkout = async (userId, workoutId, exerciseIndex) => {
  try {
    const workoutRef = doc(db, 'users', userId, 'workouts', workoutId);
    const workoutDoc = await getDoc(workoutRef);

    if (!workoutDoc.exists()) {
      throw new Error('Workout not found');
    }

    const workoutData = workoutDoc.data();
    const exercises = workoutData.exercises || [];

    // Remove the exercise at the specified index
    exercises.splice(exerciseIndex, 1);

    if (exercises.length === 0) {
      // If no exercises left, return null to signal the caller to ask about deleting the whole workout
      return null;
    }

    // Update the workout with the modified exercises array
    await updateDoc(workoutRef, { exercises });
    return exercises;
  } catch (error) {
    console.error('Error deleting exercise from workout:', error);
    throw error;
  }
};

export const updateWorkoutExercise = async (userId, workoutId, exerciseIndex, exerciseData) => {
  try {
    const workoutRef = doc(db, 'users', userId, 'workouts', workoutId);
    await updateDoc(workoutRef, {
      [`exercises.${exerciseIndex}`]: exerciseData,
    });
  } catch (error) {
    console.error('Error updating workout exercise:', error);
    throw error;
  }
};

export const getRecentWorkouts = async (userId, limitCount = 10) => {
  try {
    const workoutsRef = collection(db, 'users', userId, 'workouts');
    // Query by completedAt first (new workouts), fall back to date (old workouts)
    // Fetch more than needed to account for filtering
    const q = query(
      workoutsRef,
      orderBy('completedAt', 'desc'),
      limit(limitCount * 2)
    );
    const querySnapshot = await getDocs(q);

    // Filter out in-progress workouts
    const completed = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
      .filter(workout => workout.status !== 'in-progress')
      .slice(0, limitCount);

    return completed;
  } catch (error) {
    // If completedAt index doesn't exist yet, fall back to old date field
    try {
      const workoutsRef = collection(db, 'users', userId, 'workouts');
      const q = query(
        workoutsRef,
        orderBy('date', 'desc'),
        limit(limitCount * 2)
      );
      const querySnapshot = await getDocs(q);

      const completed = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter(workout => workout.status !== 'in-progress')
        .slice(0, limitCount);

      return completed;
    } catch (fallbackError) {
      console.error('Error getting workouts:', fallbackError);
      throw fallbackError;
    }
  }
};

export const saveUserPrefs = async (userId, prefs) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { prefs });
  } catch (error) {
    console.error('Error saving preferences:', error);
    throw error;
  }
};

export const getUserPrefs = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    return userDoc.exists() ? userDoc.data().prefs : null;
  } catch (error) {
    console.error('Error getting preferences:', error);
    return null;
  }
};

export const getLastSets = async (userId, exerciseIds) => {
  try {
    const lastSets = {};
    const promises = exerciseIds.map(async (exerciseId) => {
      const lastSetRef = doc(db, 'users', userId, 'lastSets', exerciseId);
      const lastSetDoc = await getDoc(lastSetRef);
      if (lastSetDoc.exists()) {
        lastSets[exerciseId] = lastSetDoc.data();
      }
    });
    await Promise.all(promises);
    return lastSets;
  } catch (error) {
    console.error('Error getting last sets:', error);
    return {};
  }
};

export const upsertLastSet = async (userId, exerciseId, data) => {
  try {
    const lastSetRef = doc(db, 'users', userId, 'lastSets', exerciseId);
    await setDoc(lastSetRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error upserting last set:', error);
    throw error;
  }
};

export const upsertExerciseLog = async (userId, exerciseId, entry) => {
  try {
    const logRef = doc(db, 'users', userId, 'exerciseLog', exerciseId);
    const logDoc = await getDoc(logRef);

    let existingLog = { entries: [], totalSessions: 0, bestWeight: 0, bestWeightUnit: entry.unit };
    if (logDoc.exists()) {
      existingLog = logDoc.data();
    }

    // Add new entry
    const newEntries = [...existingLog.entries, entry];

    // Cap at 50 entries, keeping the most recent
    const cappedEntries = newEntries.slice(-50);

    // Update best weight
    let bestWeight = existingLog.bestWeight || 0;
    let bestWeightUnit = existingLog.bestWeightUnit || entry.unit;
    if (entry.topWeight && entry.topWeight > bestWeight) {
      bestWeight = entry.topWeight;
      bestWeightUnit = entry.unit;
    }

    await setDoc(logRef, {
      entries: cappedEntries,
      totalSessions: existingLog.totalSessions + 1,
      bestWeight,
      bestWeightUnit,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error upserting exercise log:', error);
    throw error;
  }
};
